import { create } from 'zustand';

/**
 * One input, two scopes. In a store rather than in component state because
 * three things need it at once: the bar that owns the text, the shell that
 * opens it from `Cmd/Ctrl + K` anywhere, and the board behind it, which is
 * what "filters tiles live" actually means.
 */

export type Scope = 'room' | 'everywhere';

type SearchState = {
  open: boolean;
  query: string;
  scope: Scope;
  /** Bumped every time the shortcut fires, so the bar refocuses on a repeat. */
  focus: number;

  openSearch: (scope: Scope) => void;
  close: () => void;
  setQuery: (query: string) => void;
  setScope: (scope: Scope) => void;
};

export const useSearchStore = create<SearchState>((set, get) => ({
  open: false,
  query: '',
  scope: 'room',
  focus: 0,

  // Pressing the shortcut again while it's already open selects what's in the
  // field rather than closing it — a second Cmd+K almost always means "search
  // for something else instead". Escape is how it closes.
  openSearch: (scope) => set({ open: true, scope, focus: get().focus + 1 }),

  close: () => set({ open: false, query: '' }),
  setQuery: (query) => set({ query }),
  setScope: (scope) => set({ scope }),
}));

/**
 * What the board should filter by: nothing unless the bar is open, scoped to
 * this room, and actually holding something.
 */
export function useBoardFilter(): string | undefined {
  const open = useSearchStore((state) => state.open);
  const scope = useSearchStore((state) => state.scope);
  const query = useSearchStore((state) => state.query);

  const needle = query.trim().toLowerCase();
  return open && scope === 'room' && needle !== '' ? needle : undefined;
}
